/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import { FETCH_GET_GENDER_EMAIL_ADDRESS, POST_MODIFY_EMAIL_ADDRESS } from './actionTypes';

import { fetchGetGenderEmailAddressUtil, postModifyEmailAddressUtil } from './utils';

/**
 * Contains the action creator functions that update the ACESUserInfo slice of the store.
 *
 * @namespace ACESUserInfoActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/get_gender_email_address
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof ACESUserInfoActions
   */
  static fetchGetGenderEmailAddress = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchGetGenderEmailAddressUtil,
      FETCH_GET_GENDER_EMAIL_ADDRESS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchGetGenderEmailAddress.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserInfoActions
   */
  static resetFetchGetGenderEmailAddress = (dispatch, data) => {
    const json = {
      type: FETCH_GET_GENDER_EMAIL_ADDRESS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchGetGenderEmailAddress.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserInfoActions
   */
  static resetFetchGetGenderEmailAddressError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_GET_GENDER_EMAIL_ADDRESS.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/modify_email_address
   *
   * @param {NewACESModifyEmailAddressDetails} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserInfoActions
   */
  static postModifyEmailAddress = (dispatch, newACESModifyEmailAddressDetails, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postModifyEmailAddressUtil.bind(null, newACESModifyEmailAddressDetails),
      POST_MODIFY_EMAIL_ADDRESS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postModifyEmailAddress.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserInfoActions
   */
  static resetPostModifyEmailAddress = (dispatch, data) => {
    const json = {
      type: POST_MODIFY_EMAIL_ADDRESS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postModifyEmailAddress.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserInfoActions
   */
  static resetPostModifyEmailAddressError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_MODIFY_EMAIL_ADDRESS.failureAction);
  };
}
